import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// Témoignages marchands (vrais témoignages stockés en base).
// Les marchands déposent un témoignage via la page /temoignages ; il est
// enregistré dans `merchant_testimonials` puis affiché sur la page d'accueil.
// ─────────────────────────────────────────────────────────────

export interface MerchantTestimonial {
  id: number;
  name: string;
  role: string;
  businessName: string;
  message: string;
  rating: number;
  avatar: string;
}

export interface TestimonialInput {
  userId?: number | null;
  name: string;
  role: string;
  businessName: string;
  message: string;
  rating: number;
  avatar?: string;
}

function mapRow(row: {
  id: number;
  name: string | null;
  role_function: string | null;
  business_name: string | null;
  message: string | null;
  rating: number | null;
  avatar: string | null;
}): MerchantTestimonial {
  return {
    id: row.id,
    name: (row.name || '').trim(),
    role: (row.role_function || '').trim(),
    businessName: (row.business_name || '').trim(),
    message: (row.message || '').trim(),
    rating: Number(row.rating) || 5,
    avatar: (row.avatar || '').trim(),
  };
}

/** Enregistre un témoignage (insertion réelle en base). */
export async function submitTestimonial(input: TestimonialInput): Promise<boolean> {
  const message = (input.message || '').trim();
  const name = (input.name || '').trim();
  if (!name || !message) return false;
  const { error } = await supabase.from('merchant_testimonials').insert({
    user_id: input.userId ?? null,
    name,
    role_function: (input.role || '').trim() || null,
    business_name: (input.businessName || '').trim() || null,
    message: message.slice(0, 500),
    rating: Math.min(5, Math.max(1, Number(input.rating) || 5)),
    avatar: (input.avatar || '').trim() || null,
    // Modération : le témoignage reste « en attente » jusqu'à approbation
    // par un SuperAdmin (approuver / masquer avant publication).
    status: 'pending',
  });
  return !error;
}

/** Charge les témoignages approuvés (les plus récents d'abord). */
export function useTestimonials(limit = 6) {
  const [items, setItems] = useState<MerchantTestimonial[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('merchant_testimonials')
        .select('id, name, role_function, business_name, message, rating, avatar')
        .eq('status', 'approved')
        .order('created_at', { ascending: false })
        .limit(limit);
      if (error) throw error;
      setItems(
        ((data || []) as Parameters<typeof mapRow>[0][]).map(mapRow),
      );
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    load();
  }, [load]);

  return { items, loading, reload: load };
}